/**
 * Geocoding cache with localStorage persistence
 * Caches address -> coordinates mappings to avoid redundant API calls
 */

interface CachedGeocode {
  coords: [number, number];
  timestamp: number;
}

interface GeocodeCache {
  [address: string]: CachedGeocode;
}

const CACHE_KEY = "geocode_cache";
const CACHE_TTL = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

/**
 * Load cache from localStorage
 */
const loadCache = (): GeocodeCache => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      return JSON.parse(cached) as GeocodeCache;
    }
  } catch (error) {
    console.warn("Failed to load geocode cache:", error);
  }
  return {};
};

/**
 * Save cache to localStorage
 */
const saveCache = (cache: GeocodeCache): void => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch (error) {
    console.warn("Failed to save geocode cache:", error);
  }
};

/**
 * Normalize address string for consistent cache keys
 */
const normalizeAddress = (address: string): string => {
  return address.toLowerCase().trim().replace(/\s+/g, " ");
};

/**
 * Get cached coordinates for an address
 */
export const getCachedGeocode = (
  address: string
): [number, number] | null => {
  const cache = loadCache();
  const key = normalizeAddress(address);
  const cached = cache[key];

  if (cached) {
    // Check if cache is still valid
    if (Date.now() - cached.timestamp < CACHE_TTL) {
      return cached.coords;
    }
    // Remove expired entry
    delete cache[key];
    saveCache(cache);
  }

  return null;
};

/**
 * Cache coordinates for an address
 */
export const setCachedGeocode = (
  address: string,
  coords: [number, number]
): void => {
  const cache = loadCache();
  const key = normalizeAddress(address);

  cache[key] = {
    coords,
    timestamp: Date.now(),
  };

  // Clean up old entries (keep cache size manageable)
  const entries = Object.entries(cache);
  if (entries.length > 100) {
    // Sort by timestamp and keep newest 80 entries
    const sorted = entries.sort(
      ([, a], [, b]) => b.timestamp - a.timestamp
    );
    const newCache: GeocodeCache = {};
    sorted.slice(0, 80).forEach(([k, v]) => {
      newCache[k] = v;
    });
    saveCache(newCache);
  } else {
    saveCache(cache);
  }
};

/**
 * Geocode an address with caching
 * Returns cached result if available, otherwise fetches from Nominatim
 */
export const geocodeAddress = async (
  address: string
): Promise<[number, number] | null> => {
  // Check cache first
  const cached = getCachedGeocode(address);
  if (cached) {
    return cached;
  }

  // Fetch from Nominatim
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`,
      { headers: { "User-Agent": "StoreLocator/1.0" } }
    );
    const data = await response.json();

    if (data?.length > 0) {
      const coords: [number, number] = [
        parseFloat(data[0].lat),
        parseFloat(data[0].lon),
      ];
      // Cache the result
      setCachedGeocode(address, coords);
      return coords;
    }
  } catch (error) {
    console.error("Geocoding error for", address, error);
  }

  return null;
};

/**
 * Batch geocode multiple addresses with parallel requests (respecting rate limits)
 * @param addresses - Array of address strings to geocode
 * @param concurrency - Max concurrent requests (default: 2 to respect Nominatim limits)
 */
export const batchGeocodeAddresses = async (
  addresses: string[],
  concurrency = 2
): Promise<Map<string, [number, number]>> => {
  const results = new Map<string, [number, number]>();

  // First, check cache for all addresses
  const uncached: string[] = [];
  for (const address of addresses) {
    const cached = getCachedGeocode(address);
    if (cached) {
      results.set(address, cached);
    } else {
      uncached.push(address);
    }
  }

  // Fetch uncached addresses in parallel batches
  for (let i = 0; i < uncached.length; i += concurrency) {
    const batch = uncached.slice(i, i + concurrency);
    const promises = batch.map(async (address) => {
      // Small delay between requests in batch
      await new Promise((resolve) =>
        setTimeout(resolve, 100 * (batch.indexOf(address)))
      );
      const coords = await geocodeAddress(address);
      if (coords) {
        results.set(address, coords);
      }
    });

    await Promise.all(promises);

    // Delay between batches to respect rate limits
    if (i + concurrency < uncached.length) {
      await new Promise((resolve) => setTimeout(resolve, 200));
    }
  }

  return results;
};

/**
 * Clear all cached geocode data
 */
export const clearGeocodeCache = (): void => {
  localStorage.removeItem(CACHE_KEY);
};
